"use client";

import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { LIVE_STATUSES } from "@/contants";
import liveIndicatorGif from "@/assets/images/icon_truc_tiep.gif";
import LiveScoreMatchMinute from "../LiveScoreMatchMinute";
import LiveScoreOddsDesktopCell from "../LiveScoreOddsDesktopCell";
import LiveScoreOddsMobileCell from "../LiveScoreOddsMobileCell";

const SCORE_COLOR = "kbv-cs-text-score";
const scoreDesktopTypography =
  "text-[18px] font-medium leading-normal tracking-[2px] not-italic";
const tdPad = "p-[1px]";

const trMobile =
  "kbv-cs-bg-mobile-card relative mb-[12px] box-border grid min-h-[130px] w-full grid-cols-12 gap-x-2 gap-y-0 overflow-hidden rounded-[4px] border-0 pl-[12px] pr-[6px] align-top font-[Inter,ui-sans-serif,system-ui,sans-serif] text-[12px] leading-normal last:mb-0 md:!hidden";

const homeTeamMobile =
  "kbv-cs-text-home-team line-clamp-2 max-w-full text-center text-[12px] font-normal leading-[16px]";
const awayTeamMobile =
  "kbv-cs-text-away-team line-clamp-2 max-w-full text-center text-[12px] font-normal leading-[16px]";

const tdM = "contents";

export default function LiveScoreMatch({ match, onShowPopup, onHidePopup, activePopup, isLastInTable = false }) {
  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const { currentHomeScore, currentAwayScore } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overOdds,
    overTrend,
    overUnderValue,
    underOdds,
    underTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const popupHoverTimerRef = useRef(null);
  const clearPopupHoverTimer = () => {
    if (popupHoverTimerRef.current !== null) {
      clearTimeout(popupHoverTimerRef.current);
      popupHoverTimerRef.current = null;
    }
  };
  
  const scheduleDesktopOddsPopup = (idx) => {
    clearPopupHoverTimer();
    popupHoverTimerRef.current = setTimeout(() => {
      popupHoverTimerRef.current = null;
      if (onShowPopup) onShowPopup(match.id, idx);
    }, 500);
  };
  const cancelDesktopOddsPopup = () => {
    clearPopupHoverTimer();
    if (onHidePopup) onHidePopup();
  };

  useEffect(() => () => clearPopupHoverTimer(), []);

  const oddsCells = [
    { val: homeHandicapOdds, trend: homeHandicapTrend },
    { val: handicapValue, trend: null },
    { val: awayHandicapOdds, trend: awayHandicapTrend },
    { val: overOdds, trend: overTrend },
    { val: overUnderValue, trend: null },
    { val: underOdds, trend: underTrend },
  ];

  const oddsMobileCol = [
    "col-span-4 col-start-1 row-start-4",
    "col-span-4 col-start-5 row-start-4",
    "col-span-4 col-start-9 row-start-4",
  ];

  return (
    <>
      <tr
        className={
          isLastInTable
            ? "kbv-cs-hover-row relative hidden h-[30px] bg-white align-middle text-xs text-dark-charcoal md:!table-row"
            : "kbv-cs-hover-row relative hidden h-[30px] border-b border-t-0 border-x-0 border-solid border-[color:var(--theme-border)] bg-white align-middle text-xs text-dark-charcoal md:!table-row"
        }
      >
        <td height={30} width={34} className={`${tdPad} w-[34px] min-w-[34px] border-l-0 text-center`}>
          <div className="flex justify-center">
          </div>
        </td>

        <td
          name="timeData"
          className={`cursor-pointer ${tdPad} min-w-[50px] text-center text-[color:var(--theme-text-muted)] leading-4`}
        >
          <span>
              <LiveScoreMatchMinute
                timeStyle="desktop"
                liveScore={liveScore}
                status={statusNum}
                matchTime={match?.matchTime}
                halfStartTime={match?.halfStartTime}
              />
          </span>
        </td>

        <td height={50} className={`${tdPad} min-w-[50px] text-center`}>
          {isLive && (
            <img src={liveIndicatorGif} className="mx-auto h-3.5 w-3.5" alt="live" />
          )}
        </td>

        <td className={`${tdPad} relative text-center`}>
          <div className="inline-block align-middle">
            <a
              data-team-name={match?.homeName}
              rel="nofollow noindex"
              className="kbv-cs-text-home-team font-normal leading-4"
            >
              {match?.homeName}
            </a>
            {match?.homeRank != null && match?.homeRank !== "" && (
              <p className="m-0 leading-none">
                <span className="kbv-cs-text-muted text-xxs">
                  [{match.homeRank}]
                </span>
              </p>
            )}
          </div>
        </td>

        <td className={`cursor-pointer ${tdPad} text-center`}>
          <b className={`${SCORE_COLOR} ${scoreDesktopTypography}`}>
            {isLive ? (
              <>
                &nbsp;{currentHomeScore ?? "-"}&nbsp;-&nbsp;{currentAwayScore ?? "-"}&nbsp;
              </>
            ) : (
              "-"
            )}
          </b>
        </td>

        <td className={`${tdPad} w-[33px] min-w-[33px] text-center`}>
          <div className="min-w-[33px]">
            <div className="cursor-pointer">
              <div className="kbv-cs-text-corner-stat w-full text-center text-[11px] font-medium">
                {match?.homeCorner ?? 0}-{match?.awayCorner ?? 0}
              </div>
            </div>
          </div>
        </td>

        <td className={`${tdPad} relative text-center`}>
          <div className="inline-block">
            <a
              data-team-name={match?.awayName}
              rel="nofollow noindex"
              className="kbv-cs-text-away-team font-normal leading-4"
            >
              {match?.awayName}
            </a>
            {match?.awayRank != null && match?.awayRank !== "" && (
              <p className="m-0 leading-none">
                <span className="kbv-cs-text-muted text-xxs">
                  [{match.awayRank}]
                </span>
              </p>
            )}
          </div>
        </td>

        {oddsCells.map((item, idx) => (
          <LiveScoreOddsDesktopCell
            key={`d-${idx}`}
            tdPad={tdPad}
            isLastCell={idx === oddsCells.length - 1}
            value={item.val}
            trend={item.trend}
            match={match}
            popupOpen={
              activePopup?.matchId === match.id && activePopup?.cellIdx === idx
            }
            onMouseEnter={() => scheduleDesktopOddsPopup(idx)}
            onMouseLeave={cancelDesktopOddsPopup}
          />
        ))}

      </tr>

      <tr className={trMobile}>
        <td height={30} width={34} className={tdM}>
          <div className="col-span-12 row-start-1 flex justify-end pt-[7px]">
          </div>
        </td>

        <td  className={`${tdM} cursor-pointer`}>
          <div className="col-span-12 col-start-1 row-start-3 flex justify-center">
            <span >
              <p className="kbv-cs-text-mobile-schedule m-0 w-[150px] max-w-full text-center text-[10px] font-normal leading-[16px]">
                <LiveScoreMatchMinute
                  timeStyle="mobile"
                  liveScore={liveScore}
                  status={statusNum}
                  matchTime={match?.matchTime}
                  halfStartTime={match?.halfStartTime}
                />
              </p>
            </span>
          </div>
        </td>

        <td className={tdM} aria-hidden />

        <td className={`${tdM} text-center`}>
          <div className="col-span-4 col-start-1 row-start-2 flex min-h-[24px] flex-col items-center justify-center gap-0.5 text-center">
            <div className="flex flex-col items-center justify-center gap-0.5 leading-none">
              <a
                rel="nofollow noindex"
                className={homeTeamMobile}
              >
                {match?.homeName}
              </a>
              {match?.homeRank != null && match?.homeRank !== "" && (
                <p className="m-0 leading-none">
                  <span className="kbv-cs-text-muted text-[10px] font-normal leading-[14px]">[{match.homeRank}]</span>
                </p>
              )}
            </div>
          </div>
        </td>

        <td className={`${tdM} cursor-pointer text-center`}>
          <div className="col-span-4 col-start-5 row-start-2 flex min-h-[24px] flex-col items-center justify-center gap-1">
            {isLive ? (
              <div className="flex items-center justify-center">
                {homeLogo ? (
                  <img loading="lazy" src={homeLogo} className="inline-block h-3.5 w-3.5 shrink-0 object-contain" alt="" />
                ) : null}
                <b
                  className={`mx-0 text-center text-[16px] font-bold leading-[19.36px] tracking-[4px] [&:not(:last-child)]:mr-[7px] ${SCORE_COLOR}`}
                >
                  &nbsp;{currentHomeScore ?? "-"}&nbsp;-&nbsp;{currentAwayScore ?? "-"}&nbsp;
                </b>
                {awayLogo ? (
                  <img loading="lazy" src={awayLogo} className="inline-block h-3.5 w-3.5 shrink-0 object-contain" alt="" />
                ) : null}
              </div>
            ) : (
              <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-x-4 px-1">
                <div className="flex items-center justify-end">
                  {homeLogo ? (
                    <img loading="lazy" src={homeLogo} className="inline-block h-3.5 w-3.5 shrink-0 object-contain" alt="" />
                  ) : (
                    <span className="inline-block h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                </div>
                <b
                  className={`shrink-0 px-2 text-center text-[16px] font-bold leading-[19.36px] ${SCORE_COLOR}`}
                >
                  -
                </b>
                <div className="flex items-center justify-start">
                  {awayLogo ? (
                    <img loading="lazy" src={awayLogo} className="inline-block h-3.5 w-3.5 shrink-0 object-contain" alt="" />
                  ) : (
                    <span className="inline-block h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                </div>
              </div>
            )}
          </div>
        </td>

        <td className={tdM}>
          <div className="hidden" aria-hidden />
        </td>

        <td className={`${tdM} text-center`}>
          <div className="col-span-4 col-start-9 row-start-2 flex min-h-[24px] flex-col items-center justify-center text-center">
            <div className="flex flex-col items-center justify-center gap-0.5 leading-none">
              <a
                rel="nofollow noindex"
                className={awayTeamMobile}
              >
                {match?.awayName}
              </a>
              {match?.awayRank != null && match?.awayRank !== "" && (
                <p className="m-0 leading-none">
                  <span className="kbv-cs-text-muted text-[10px] font-normal leading-[14px]">[{match.awayRank}]</span>
                </p>
              )}
            </div>
          </div>
        </td>

        {oddsCells.map((item, idx) => (
          <LiveScoreOddsMobileCell
            key={`m-${idx}`}
            tdM={tdM}
            cellIdx={idx}
            value={item.val}
            trend={item.trend}
            oddsMobileCol={oddsMobileCol}
          />
        ))}

        <td colSpan={1} className={tdM}>
          
        </td>
      </tr>
    </>
  );
}
